import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { saveAs } from "file-saver";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import { useSnackbar } from "notistack";
import { fetchAvailableQRCodes } from "../api/qrCodes";

const buildCanvasId = (qrCodeId) => `admin-qr-code-${qrCodeId}`;

const sanitizeFileName = (value) =>
  (value || "qr")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const downloadCanvasAsPng = (canvasId, fileName) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;

  canvas.toBlob((blob) => {
    if (blob) {
      saveAs(blob, fileName);
    }
  });
};

const downloadCanvasAsPdf = (canvasId, title, fileName) => {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: [300, 420],
  });

  const imageData = canvas.toDataURL("image/png");
  pdf.text(title, 40, 30);
  pdf.addImage(imageData, "PNG", 22, 50, 256, 256);
  pdf.save(fileName);
};

const AdminQRPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const loadQRCodes = async () => {
      try {
        const availableQRCodes = await fetchAvailableQRCodes();
        setQrCodes(availableQRCodes);
        setErrorMessage("");
      } catch (error) {
        if (error.status === 404) {
          setQrCodes([]);
          setErrorMessage("");
          return;
        }

        setErrorMessage(error.message || "No se pudieron cargar los QRs.");
        enqueueSnackbar(error.message || "No se pudieron cargar los QRs.", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadQRCodes();
  }, [enqueueSnackbar]);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box
        sx={{
          textAlign: "center",
          p: { xs: 3, md: 4 },
          border: "1px solid #e0e0e0",
          borderRadius: 4,
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, color: "#1B8D4B" }}>
          QRs Disponibles
        </Typography>
        <Typography sx={{ mb: 4, color: "#666" }}>
          Se listan los QRs activos disponibles para asignacion y descarga.
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#1B8D4B" }} />
          </Box>
        )}

        {!loading && errorMessage && (
          <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
            {errorMessage}
          </Alert>
        )}

        {!loading && !errorMessage && qrCodes.length === 0 && (
          <Alert severity="info">No hay QRs activos disponibles.</Alert>
        )}

        {!loading && !errorMessage && qrCodes.length > 0 && (
          <Grid container spacing={3}>
            {qrCodes.map((qrCode) => {
              const canvasId = buildCanvasId(qrCode.id);
              const title = qrCode.label || `QR ${qrCode.id}`;
              const fileBaseName = sanitizeFileName(qrCode.label || `qr-${qrCode.id}`);

              return (
                <Grid item xs={12} sm={6} md={4} key={qrCode.id}>
                  <Paper
                    sx={{
                      height: "100%",
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: "#fff",
                      border: "1px solid #e0f2e9",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#fff",
                        borderRadius: 2,
                        boxShadow: 1,
                      }}
                    >
                      <QRCodeCanvas
                        id={canvasId}
                        value={qrCode.url}
                        size={220}
                        level="H"
                        includeMargin
                      />
                    </Box>

                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#1B8D4B",
                          fontWeight: 700,
                          mb: 1,
                          wordBreak: "break-word",
                        }}
                      >
                        {title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#555",
                          wordBreak: "break-word",
                          mb: 1,
                        }}
                      >
                        {qrCode.url}
                      </Typography>
                      {qrCode.description && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#777", wordBreak: "break-word" }}
                        >
                          {qrCode.description}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                        mt: "auto",
                      }}
                    >
                      <Button
                        onClick={() =>
                          downloadCanvasAsPng(canvasId, `${fileBaseName}.png`)
                        }
                        variant="contained"
                        sx={{
                          bgcolor: "#1B8D4B",
                          "&:hover": { bgcolor: "#14703B" },
                        }}
                      >
                        Descargar PNG
                      </Button>
                      <Button
                        onClick={() =>
                          downloadCanvasAsPdf(
                            canvasId,
                            title,
                            `${fileBaseName}.pdf`
                          )
                        }
                        variant="outlined"
                        sx={{
                          borderColor: "#1B8D4B",
                          color: "#1B8D4B",
                          "&:hover": {
                            bgcolor: "#E0F2E9",
                            borderColor: "#14703B",
                            color: "#14703B",
                          },
                        }}
                      >
                        Descargar PDF
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default AdminQRPage;
